import type { Prisma, PrismaClient } from '@prisma/client';
import Decimal from 'decimal.js';
import { Router, type Request } from 'express';
import { z } from 'zod';
import { audit } from '../../core/audit.js';
import type { Config } from '../../core/config.js';
import { HttpError } from '../../core/errors.js';
import { asyncHandler, clientIp, pageParams, parse } from '../../core/http.js';
import { idempotent } from '../../core/idempotency.js';
import { accountBalances } from '../accounting/posting.service.js';
import { authenticate, permissionsGranted } from '../auth/auth.middleware.js';
import { csvLine } from '../csv/csv.parser.js';
import { ageBucket, outstanding, type AgeBucket } from './calc.js';
import {
  DOC_KINDS,
  DOC_PERM,
  addDays,
  approveDocument,
  assertWithinLimit,
  convertDocument,
  docInclude,
  documentInputSchema,
  documentView,
  duplicateDocument,
  isoOf,
  kindOf,
  loadDocument,
  logEvent,
  saveDraft,
  toDate,
  visibleWhere,
  voidDocument,
  type DocKind,
} from './documents.service.js';
import { cancelDocumentReminders } from './reminders.service.js';
import {
  applyAdvances,
  createReceipt,
  receiptInputSchema,
  receiptView,
  voidReceipt,
} from './receipts.service.js';
import { scheduleEfris } from '../efris/efris.service.js';
import { autoSendOnApproval, autoSendReceipt } from './send.service.js';

interface Deps {
  db: PrismaClient;
  config: Config;
}

const OPEN = ['approved', 'sent', 'partially_paid'];

export function createSalesRouter({ db, config }: Deps) {
  const router = Router();
  router.use('/sales', authenticate({ db, config }), idempotent(db));

  const actor = (req: Request) => ({
    companyId: req.auth!.companyId,
    userId: req.auth!.userId,
    ip: clientIp(req),
  });
  const can = (req: Request, p: string) => permissionsGranted(req.auth!.permissions, p);
  const need = (req: Request, kind: DocKind, action: string) => {
    if (!can(req, `${DOC_PERM[kind]}.${action}`))
      throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
  };
  const kindParam = (v: unknown): DocKind => {
    if (!DOC_KINDS.includes(v as DocKind))
      throw new HttpError(400, 'VALIDATION_ERROR', `type must be one of ${DOC_KINDS.join(', ')}`);
    return v as DocKind;
  };

  const partyNames = async (ids: number[]) => {
    const rows = await db.party.findMany({
      where: { id: { in: [...new Set(ids)] } },
      select: { id: true, name: true },
    });
    return new Map(rows.map((p) => [p.id, p.name]));
  };

  // ───────────── documents: list ─────────────
  router.get(
    '/sales/documents',
    asyncHandler(async (req, res) => {
      const q = req.query as Record<string, string | undefined>;
      const kind = kindParam(q.type);
      need(req, kind, 'view');
      const { page, pageSize, skip, take } = pageParams(req.query as Record<string, unknown>);
      const today = toDate(new Date().toISOString().slice(0, 10));
      const search = q.search?.trim();
      const partyIds = search
        ? (
            await db.party.findMany({
              where: { companyId: req.auth!.companyId, name: { contains: search } },
              select: { id: true },
              take: 50,
            })
          ).map((p) => p.id)
        : [];
      const where: Prisma.SalesDocumentWhereInput = {
        AND: [
          visibleWhere(req.auth!, kind),
          q.status === 'overdue'
            ? { status: { in: OPEN }, dueDate: { lt: today } }
            : q.status
              ? { status: q.status }
              : {},
          q.open === 'true' ? { status: { in: OPEN } } : {},
          q.partyId ? { partyId: Number(q.partyId) } : {},
          q.from || q.to
            ? {
                issueDate: {
                  ...(q.from ? { gte: toDate(q.from) } : {}),
                  ...(q.to ? { lte: toDate(q.to) } : {}),
                },
              }
            : {},
          search
            ? {
                OR: [
                  { number: { contains: search } },
                  { reference: { contains: search } },
                  ...(partyIds.length ? [{ partyId: { in: partyIds } }] : []),
                ],
              }
            : {},
        ],
      };
      const sortKey = ['issueDate', 'dueDate', 'total', 'number', 'createdAt'].includes(
        String(q.sort),
      )
        ? String(q.sort)
        : 'createdAt';
      const [total, rows, agg] = await Promise.all([
        db.salesDocument.count({ where }),
        db.salesDocument.findMany({
          where,
          include: docInclude,
          orderBy: { [sortKey]: q.dir === 'asc' ? 'asc' : 'desc' },
          skip,
          take,
        }),
        db.salesDocument.aggregate({
          where,
          _sum: { total: true, amountPaid: true, amountCredited: true },
        }),
      ]);
      const names = await partyNames(rows.map((r) => r.partyId));
      res.json({
        items: rows.map((r) => ({
          ...documentView(r, { partyName: names.get(r.partyId) }),
          lines: undefined,
        })),
        total,
        page,
        pageSize,
        totals: {
          amount: new Decimal((agg._sum.total ?? 0).toString()).toFixed(4),
          outstanding: new Decimal((agg._sum.total ?? 0).toString())
            .minus((agg._sum.amountPaid ?? 0).toString())
            .minus((agg._sum.amountCredited ?? 0).toString())
            .toFixed(4),
        },
      });
    }),
  );

  // ───────────── exports (CSV, one row per line; formula-neutralised) ─────────────
  router.get(
    '/sales/documents/export.csv',
    asyncHandler(async (req, res) => {
      const q = req.query as Record<string, string | undefined>;
      const kind = kindParam(q.type);
      need(req, kind, 'export');
      const where: Prisma.SalesDocumentWhereInput = {
        AND: [
          visibleWhere(req.auth!, kind),
          q.status ? { status: q.status } : {},
          q.partyId ? { partyId: Number(q.partyId) } : {},
          q.from || q.to
            ? {
                issueDate: {
                  ...(q.from ? { gte: toDate(q.from) } : {}),
                  ...(q.to ? { lte: toDate(q.to) } : {}),
                },
              }
            : {},
        ],
      };
      const header = [
        'number',
        'status',
        'client_name',
        'issue_date',
        kind === 'invoice' ? 'due_date' : 'valid_until',
        'currency',
        'exchange_rate',
        'reference',
        'notes',
        'terms',
        'line_description',
        'item_sku',
        'quantity',
        'unit',
        'unit_price',
        'discount_percent',
        'vat_code',
        'revenue_account',
        'document_total',
        'amount_paid',
      ];
      res.setHeader('Content-Type', 'text/csv; charset=utf-8');
      res.setHeader('Content-Disposition', `attachment; filename="${kind}s.csv"`);
      res.write('﻿' + csvLine(header) + '\r\n');
      let count = 0;
      let cursor = 0;
      for (;;) {
        const rows = await db.salesDocument.findMany({
          where,
          include: docInclude,
          orderBy: { id: 'asc' },
          take: 200,
          ...(cursor ? { cursor: { id: cursor }, skip: 1 } : {}),
        });
        if (rows.length === 0) break;
        const names = await partyNames(rows.map((r) => r.partyId));
        const itemIds = rows.flatMap((r) =>
          r.lines.map((l) => l.itemId).filter((i): i is number => !!i),
        );
        const skus = new Map(
          (
            await db.item.findMany({
              where: { id: { in: itemIds } },
              select: { id: true, sku: true },
            })
          ).map((i) => [i.id, i.sku]),
        );
        for (const d of rows) {
          for (const l of d.lines.filter((x) => x.kind === 'item')) {
            res.write(
              csvLine([
                d.number,
                d.status,
                names.get(d.partyId),
                isoOf(d.issueDate),
                isoOf(kind === 'invoice' ? d.dueDate : d.validUntil),
                d.currency,
                d.exchangeRate.toString(),
                d.reference,
                d.notes,
                d.terms,
                l.description,
                l.itemId ? skus.get(l.itemId) : '',
                l.quantity.toString(),
                l.unit,
                l.unitPrice.toString(),
                l.discountType === 'percent' ? l.discountValue.toString() : '0',
                l.vatCode,
                l.revenueAccountKey,
                d.total.toString(),
                d.amountPaid.toString(),
              ]) + '\r\n',
            );
            count += 1;
          }
        }
        cursor = rows[rows.length - 1]!.id;
      }
      await db.exportLog.create({
        data: {
          companyId: req.auth!.companyId,
          userId: req.auth!.userId,
          entity: `${kind}s`,
          filters: req.query as Prisma.InputJsonValue,
          rowCount: count,
        },
      });
      await audit(db, actor(req), {
        action: 'export.csv',
        entity: `${kind}s`,
        after: { rows: count, filters: req.query },
      });
      res.end();
    }),
  );

  // ───────────── documents: detail ─────────────
  router.get(
    '/sales/documents/:id',
    asyncHandler(async (req, res) => {
      const doc = await loadDocument(db, req.auth!, Number(req.params.id));
      const kind = kindOf(doc);
      need(req, kind, 'view');
      const [party, contact, events, children, source, allocations, applications, creditNotes] =
        await Promise.all([
          db.party.findUnique({ where: { id: doc.partyId }, include: { contacts: true } }),
          doc.contactId ? db.partyContact.findUnique({ where: { id: doc.contactId } }) : null,
          db.documentEvent.findMany({
            where: { entityType: 'sales_document', entityId: doc.id },
            orderBy: { id: 'asc' },
          }),
          db.salesDocument.findMany({
            where: { sourceDocumentId: doc.id },
            select: { id: true, docType: true, number: true, status: true, total: true },
          }),
          doc.sourceDocumentId
            ? db.salesDocument.findUnique({
                where: { id: doc.sourceDocumentId },
                select: { id: true, docType: true, number: true },
              })
            : null,
          kind === 'invoice'
            ? db.receiptAllocation.findMany({
                where: { invoiceId: doc.id, receipt: { status: 'posted' } },
                include: { receipt: true },
              })
            : [],
          kind === 'invoice'
            ? db.advanceApplication.findMany({ where: { invoiceId: doc.id } })
            : [],
          kind === 'invoice'
            ? db.salesDocument.findMany({
                where: { linkedInvoiceId: doc.id, status: { not: 'void' } },
                select: { id: true, number: true, total: true },
              })
            : [],
        ]);
      const users = await db.user.findMany({
        where: { id: { in: events.map((e) => e.userId).filter((u): u is number => !!u) } },
        select: { id: true, name: true },
      });
      const comms = await db.communication.findMany({
        where: { companyId: doc.companyId, docType: kind, docId: doc.id },
        orderBy: { id: 'desc' },
        omit: { bodyText: true },
        take: 50,
      });
      res.json({
        ...documentView(doc, { partyName: party?.name }),
        party: party && {
          id: party.id,
          name: party.name,
          tin: party.tin,
          vatRegistered: party.vatRegistered,
          address: party.address,
          city: party.city,
          paymentTermsDays: party.paymentTermsDays,
          contacts: party.contacts.map((c) => ({
            id: c.id,
            name: c.name,
            email: c.email,
            phone: c.phone,
            whatsappNumber: c.whatsappNumber,
            receivesDocuments: c.receivesDocuments,
          })),
        },
        contact: contact && { id: contact.id, name: contact.name },
        source,
        children: children.map((c) => ({ ...c, total: c.total.toFixed(4) })),
        payments: allocations.map((a) => ({
          receiptId: a.receiptId,
          receiptNo: a.receipt.receiptNo,
          date: isoOf(a.receipt.receiptDate),
          amount: a.amount.toFixed(4),
        })),
        advancesApplied: applications.map((a) => ({
          amount: a.amount.toFixed(4),
          date: a.createdAt,
        })),
        creditNotes: creditNotes.map((c) => ({
          id: c.id,
          number: c.number,
          total: c.total.toFixed(4),
        })),
        events: events.map((e) => ({
          id: e.id,
          type: e.type,
          note: e.note,
          at: e.createdAt,
          by: users.find((u) => u.id === e.userId)?.name ?? null,
        })),
        communications: comms,
      });
    }),
  );

  // ───────────── documents: create / edit ─────────────
  const withType = documentInputSchema.and(z.object({ type: z.enum(DOC_KINDS) }));

  router.post(
    '/sales/documents',
    asyncHandler(async (req, res) => {
      const body = parse(withType, req.body);
      need(req, body.type, 'create');
      const doc = await db.$transaction(async (tx) => {
        const created = await saveDraft(tx, { auth: req.auth!, kind: body.type, input: body });
        await logEvent(tx, {
          companyId: req.auth!.companyId,
          entityType: 'sales_document',
          entityId: created.id,
          type: 'created',
          userId: req.auth!.userId,
        });
        await audit(tx, actor(req), {
          action: `${body.type}.created`,
          entity: 'sales_document',
          entityId: created.id,
          after: {
            partyId: created.partyId,
            total: created.total.toString(),
            currency: created.currency,
          },
        });
        return created;
      });
      res.status(201).json(documentView(doc));
    }),
  );

  router.put(
    '/sales/documents/:id',
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const existing = await loadDocument(db, req.auth!, id);
      const kind = kindOf(existing);
      need(req, kind, 'edit');
      const body = parse(documentInputSchema, req.body);
      const doc = await db.$transaction(async (tx) => {
        const updated = await saveDraft(tx, {
          auth: req.auth!,
          kind,
          id,
          input: { ...body, sourceDocumentId: existing.sourceDocumentId },
        });
        await audit(tx, actor(req), {
          action: `${kind}.updated`,
          entity: 'sales_document',
          entityId: id,
          before: { total: existing.total.toString() },
          after: { total: updated.total.toString() },
        });
        return updated;
      });
      res.json(documentView(doc));
    }),
  );

  // ───────────── workflow ─────────────
  router.post(
    '/sales/documents/:id/submit',
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const doc = await loadDocument(db, req.auth!, id);
      const kind = kindOf(doc);
      need(req, kind, 'edit');
      if (doc.status !== 'draft')
        throw new HttpError(409, 'INVALID_STATE', 'Only drafts can be submitted for approval');
      const updated = await db.salesDocument.update({
        where: { id },
        data: { status: 'pending_approval', submittedAt: new Date() },
        include: docInclude,
      });
      await logEvent(db, {
        companyId: doc.companyId,
        entityType: 'sales_document',
        entityId: id,
        type: 'submitted',
        userId: req.auth!.userId,
      });
      res.json(documentView(updated));
    }),
  );

  router.post(
    '/sales/documents/:id/approve',
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const doc = await loadDocument(db, req.auth!, id);
      const kind = kindOf(doc);
      need(req, kind, 'approve');
      assertWithinLimit(req.auth!, doc.baseTotal);
      const approved = await db.$transaction(async (tx) => {
        const result = await approveDocument(tx, req.auth!, id);
        await audit(tx, actor(req), {
          action: `${kind}.approved`,
          entity: 'sales_document',
          entityId: id,
          after: { number: result.number, total: result.total.toString() },
        });
        return result;
      });
      await autoSendOnApproval(db, config, req.auth!, approved).catch(() => undefined);
      await scheduleEfris(db, approved); // optional and never blocks: a no-op unless EFRIS is enabled
      res.json(documentView(approved));
    }),
  );

  router.post(
    '/sales/documents/:id/reject',
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const { reason } = parse(z.object({ reason: z.string().trim().min(3).max(300) }), req.body);
      const doc = await loadDocument(db, req.auth!, id);
      need(req, kindOf(doc), 'approve');
      if (doc.status !== 'pending_approval')
        throw new HttpError(
          409,
          'INVALID_STATE',
          'Only documents awaiting approval can be rejected',
        );
      const updated = await db.salesDocument.update({
        where: { id },
        data: { status: 'draft' },
        include: docInclude,
      });
      await logEvent(db, {
        companyId: doc.companyId,
        entityType: 'sales_document',
        entityId: id,
        type: 'rejected',
        userId: req.auth!.userId,
        note: reason,
      });
      res.json(documentView(updated));
    }),
  );

  router.post(
    '/sales/documents/:id/void',
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const { reason } = parse(z.object({ reason: z.string().trim().min(3).max(300) }), req.body);
      const doc = await loadDocument(db, req.auth!, id);
      const kind = kindOf(doc);
      need(req, kind, 'delete');
      const updated = await db.$transaction(async (tx) => {
        const v = await voidDocument(tx, req.auth!, id, reason);
        await audit(tx, actor(req), {
          action: `${kind}.voided`,
          entity: 'sales_document',
          entityId: id,
          before: { status: doc.status },
          after: { status: 'void', reason },
        });
        return v;
      });
      await cancelDocumentReminders(db, kind, id);
      res.json(documentView(updated));
    }),
  );

  router.post(
    '/sales/documents/:id/duplicate',
    asyncHandler(async (req, res) => {
      const src = await loadDocument(db, req.auth!, Number(req.params.id));
      need(req, kindOf(src), 'create');
      const copy = await db.$transaction((tx) => duplicateDocument(tx, req.auth!, src.id));
      res.status(201).json(documentView(copy));
    }),
  );

  router.post(
    '/sales/documents/:id/convert',
    asyncHandler(async (req, res) => {
      const body = parse(
        z.object({
          to: z.enum(['proforma', 'invoice']),
          percent: z
            .union([z.string(), z.number()])
            .transform((v) => String(v))
            .refine((v) => /^\d{1,3}(\.\d{1,4})?$/.test(v))
            .default('100'),
        }),
        req.body,
      );
      const src = await loadDocument(db, req.auth!, Number(req.params.id));
      need(req, body.to, 'create');
      need(req, kindOf(src), 'view');
      const created = await db.$transaction(async (tx) => {
        const doc = await convertDocument(tx, req.auth!, {
          sourceId: src.id,
          to: body.to,
          percent: new Decimal(body.percent),
        });
        await audit(tx, actor(req), {
          action: `${kindOf(src)}.converted`,
          entity: 'sales_document',
          entityId: src.id,
          after: { to: body.to, targetId: doc.id, percent: body.percent },
        });
        return doc;
      });
      const fresh = await loadDocument(db, req.auth!, src.id);
      if (fresh.status === 'converted') await cancelDocumentReminders(db, kindOf(src), src.id);
      res.status(201).json(documentView(created));
    }),
  );

  // Staff can record the client's answer on their behalf (phone call, WhatsApp reply).
  router.post(
    '/sales/documents/:id/respond',
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const body = parse(
        z.object({
          outcome: z.enum(['accepted', 'declined', 'replied']),
          name: z.string().trim().max(200).optional(),
          note: z.string().trim().max(500).optional(),
        }),
        req.body,
      );
      const doc = await loadDocument(db, req.auth!, id);
      const kind = kindOf(doc);
      need(req, kind, 'edit');
      if (!['quotation', 'proforma'].includes(kind))
        throw new HttpError(
          409,
          'INVALID_STATE',
          'Only quotations and proformas record client responses',
        );
      if (!['sent', 'viewed', 'approved', 'accepted'].includes(doc.status))
        throw new HttpError(409, 'INVALID_STATE', 'Send the document first');
      const now = new Date();
      const data: Prisma.SalesDocumentUpdateInput = { respondedAt: now };
      if (body.outcome === 'accepted')
        Object.assign(data, {
          status: 'accepted',
          acceptedAt: now,
          acceptedByName: body.name ?? 'Recorded by staff',
        });
      if (body.outcome === 'declined')
        Object.assign(data, {
          status: 'declined',
          declinedAt: now,
          declineReason: body.note ?? null,
        });
      const updated = await db.salesDocument.update({ where: { id }, data, include: docInclude });
      await logEvent(db, {
        companyId: doc.companyId,
        entityType: 'sales_document',
        entityId: id,
        type: body.outcome === 'replied' ? 'responded' : body.outcome,
        userId: req.auth!.userId,
        note: body.note ?? body.name,
      });
      await cancelDocumentReminders(db, kind, id);
      res.json(documentView(updated));
    }),
  );

  // ───────────── receipts ─────────────
  router.get(
    '/sales/receipts',
    asyncHandler(async (req, res) => {
      if (!can(req, 'sales.receipt.view'))
        throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
      const { page, pageSize, skip, take } = pageParams(req.query as Record<string, unknown>);
      const q = req.query as Record<string, string | undefined>;
      const search = q.search?.trim();
      const partyIds = search
        ? (
            await db.party.findMany({
              where: { companyId: req.auth!.companyId, name: { contains: search } },
              select: { id: true },
              take: 50,
            })
          ).map((p) => p.id)
        : [];
      const where: Prisma.PaymentReceiptWhereInput = {
        companyId: req.auth!.companyId,
        ...(req.auth!.scope === 'own' ? { createdBy: req.auth!.userId } : {}),
        ...(q.partyId ? { partyId: Number(q.partyId) } : {}),
        ...(q.status ? { status: q.status } : {}),
        ...(search
          ? {
              OR: [
                { receiptNo: { contains: search } },
                { reference: { contains: search } },
                ...(partyIds.length ? [{ partyId: { in: partyIds } }] : []),
              ],
            }
          : {}),
      };
      const [total, rows] = await Promise.all([
        db.paymentReceipt.count({ where }),
        db.paymentReceipt.findMany({
          where,
          include: { allocations: true },
          orderBy: { id: 'desc' },
          skip,
          take,
        }),
      ]);
      res.json({
        items: await Promise.all(rows.map((r) => receiptView(db, r))),
        total,
        page,
        pageSize,
      });
    }),
  );

  // Accounts a receipt can be deposited into (no banking permission needed, only the right to record receipts).
  router.get(
    '/sales/receipt-accounts',
    asyncHandler(async (req, res) => {
      if (!can(req, 'sales.receipt.create') && !can(req, 'sales.receipt.view'))
        throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
      const rows = await db.financialAccount.findMany({
        where: { companyId: req.auth!.companyId, status: 'active' },
        orderBy: [{ kind: 'asc' }, { name: 'asc' }],
        select: { id: true, name: true, kind: true, currency: true },
      });
      res.json({ items: rows });
    }),
  );

  router.get(
    '/sales/receipts/export.csv',
    asyncHandler(async (req, res) => {
      if (!can(req, 'sales.receipt.export'))
        throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
      const where: Prisma.PaymentReceiptWhereInput = {
        companyId: req.auth!.companyId,
        ...(req.auth!.scope === 'own' ? { createdBy: req.auth!.userId } : {}),
      };
      res.setHeader('Content-Type', 'text/csv; charset=utf-8');
      res.setHeader('Content-Disposition', 'attachment; filename="receipts.csv"');
      res.write(
        '﻿' +
          csvLine([
            'receipt_number',
            'status',
            'client_name',
            'receipt_date',
            'account',
            'amount',
            'wht_withheld',
            'currency',
            'exchange_rate',
            'reference',
            'invoice_number',
            'allocated_amount',
          ]) +
          '\r\n',
      );
      const rows = await db.paymentReceipt.findMany({
        where,
        include: { allocations: true },
        orderBy: { id: 'asc' },
      });
      const names = await partyNames(rows.map((r) => r.partyId));
      const accts = new Map(
        (
          await db.financialAccount.findMany({
            where: { companyId: req.auth!.companyId },
            select: { id: true, name: true },
          })
        ).map((a) => [a.id, a.name]),
      );
      const invs = new Map(
        (
          await db.salesDocument.findMany({
            where: { id: { in: rows.flatMap((r) => r.allocations.map((a) => a.invoiceId)) } },
            select: { id: true, number: true },
          })
        ).map((i) => [i.id, i.number]),
      );
      let count = 0;
      for (const r of rows) {
        const base = [
          r.receiptNo,
          r.status,
          names.get(r.partyId),
          isoOf(r.receiptDate),
          accts.get(r.financialAccountId),
          r.amount.toString(),
          r.whtWithheld.toString(),
          r.currency,
          r.exchangeRate.toString(),
          r.reference,
        ];
        const lines = r.allocations.length ? r.allocations : [null];
        for (const a of lines) {
          res.write(
            csvLine([...base, a ? invs.get(a.invoiceId) : '', a ? a.amount.toString() : '']) +
              '\r\n',
          );
          count += 1;
        }
      }
      await db.exportLog.create({
        data: {
          companyId: req.auth!.companyId,
          userId: req.auth!.userId,
          entity: 'receipts',
          filters: req.query as Prisma.InputJsonValue,
          rowCount: count,
        },
      });
      await audit(db, actor(req), {
        action: 'export.csv',
        entity: 'receipts',
        after: { rows: count },
      });
      res.end();
    }),
  );

  router.get(
    '/sales/receipts/:id',
    asyncHandler(async (req, res) => {
      if (!can(req, 'sales.receipt.view'))
        throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
      const r = await db.paymentReceipt.findFirst({
        where: {
          id: Number(req.params.id),
          companyId: req.auth!.companyId,
          ...(req.auth!.scope === 'own' ? { createdBy: req.auth!.userId } : {}),
        },
        include: { allocations: true },
      });
      if (!r) throw new HttpError(404, 'NOT_FOUND', 'Receipt not found');
      const events = await db.documentEvent.findMany({
        where: { entityType: 'receipt', entityId: r.id },
        orderBy: { id: 'asc' },
      });
      const advance = await db.customerAdvance.findFirst({
        where: { sourceType: 'receipt', sourceId: r.id },
        include: { applications: true },
      });
      const comms = await db.communication.findMany({
        where: { companyId: r.companyId, docType: 'receipt', docId: r.id },
        orderBy: { id: 'desc' },
        omit: { bodyText: true },
        take: 50,
      });
      res.json({
        ...(await receiptView(db, r)),
        advance: advance && {
          id: advance.id,
          original: advance.originalAmount.toFixed(4),
          remaining: advance.remainingAmount.toFixed(4),
        },
        events: events.map((e) => ({ id: e.id, type: e.type, note: e.note, at: e.createdAt })),
        communications: comms,
      });
    }),
  );

  router.post(
    '/sales/receipts',
    asyncHandler(async (req, res) => {
      if (!can(req, 'sales.receipt.create'))
        throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
      const input = parse(receiptInputSchema, req.body);
      const receipt = await db.$transaction(async (tx) => {
        const r = await createReceipt(tx, req.auth!, input);
        await audit(tx, actor(req), {
          action: 'receipt.created',
          entity: 'payment_receipt',
          entityId: r.id,
          after: {
            receiptNo: r.receiptNo,
            amount: r.amount.toString(),
            allocated: r.allocatedAmount.toString(),
            advance: r.advanceAmount.toString(),
          },
        });
        return r;
      });
      await autoSendReceipt(db, config, req.auth!, receipt.id).catch(() => undefined);
      const full = await db.paymentReceipt.findUniqueOrThrow({
        where: { id: receipt.id },
        include: { allocations: true },
      });
      res.status(201).json(await receiptView(db, full));
    }),
  );

  router.post(
    '/sales/receipts/:id/void',
    asyncHandler(async (req, res) => {
      if (!can(req, 'sales.receipt.delete'))
        throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
      const { reason } = parse(z.object({ reason: z.string().trim().min(3).max(300) }), req.body);
      const r = await db.$transaction(async (tx) => {
        const voided = await voidReceipt(tx, req.auth!, Number(req.params.id), reason);
        await audit(tx, actor(req), {
          action: 'receipt.voided',
          entity: 'payment_receipt',
          entityId: voided.id,
          after: { reason },
        });
        return voided;
      });
      res.json(
        await receiptView(
          db,
          await db.paymentReceipt.findUniqueOrThrow({
            where: { id: r.id },
            include: { allocations: true },
          }),
        ),
      );
    }),
  );

  // ───────────── customer advances ─────────────
  router.get(
    '/sales/advances',
    asyncHandler(async (req, res) => {
      if (!can(req, 'sales.receipt.view'))
        throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
      const partyId = Number(req.query.partyId);
      const rows = await db.customerAdvance.findMany({
        where: {
          companyId: req.auth!.companyId,
          ...(partyId ? { partyId } : {}),
          remainingAmount: { gt: 0 },
        },
        orderBy: { id: 'asc' },
      });
      res.json({
        items: rows.map((a) => ({
          id: a.id,
          partyId: a.partyId,
          currency: a.currency,
          original: a.originalAmount.toFixed(4),
          remaining: a.remainingAmount.toFixed(4),
          sourceType: a.sourceType,
          sourceId: a.sourceId,
          proformaId: a.proformaId,
        })),
      });
    }),
  );

  router.post(
    '/sales/advances/:id/apply',
    asyncHandler(async (req, res) => {
      if (!can(req, 'sales.receipt.create'))
        throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
      const body = parse(
        z.object({
          allocations: z
            .array(
              z.object({
                invoiceId: z.number().int().positive(),
                amount: z.union([z.string(), z.number()]).transform(String),
              }),
            )
            .min(1)
            .max(50),
        }),
        req.body,
      );
      await db.$transaction(async (tx) => {
        await applyAdvances(tx, req.auth!, Number(req.params.id), body.allocations);
        await audit(tx, actor(req), {
          action: 'advance.applied',
          entity: 'customer_advance',
          entityId: Number(req.params.id),
          after: body,
        });
      });
      res.json({ ok: true });
    }),
  );

  // ───────────── receivables ageing ─────────────
  router.get(
    '/sales/reports/ageing',
    asyncHandler(async (req, res) => {
      if (!can(req, 'accounting.report.view') && !can(req, 'sales.invoice.view'))
        throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
      const asOfStr =
        typeof req.query.asOf === 'string' ? req.query.asOf : new Date().toISOString().slice(0, 10);
      const asOf = toDate(asOfStr);
      const invoices = await db.salesDocument.findMany({
        where: { AND: [visibleWhere(req.auth!, 'invoice'), { status: { in: OPEN } }] },
      });
      const names = await partyNames(invoices.map((i) => i.partyId));
      const empty = () => ({
        current: new Decimal(0),
        d1_30: new Decimal(0),
        d31_60: new Decimal(0),
        d61_90: new Decimal(0),
        d90_plus: new Decimal(0),
        total: new Decimal(0),
      });
      const byParty = new Map<
        number,
        ReturnType<typeof empty> & { partyName: string; currencies: Set<string> }
      >();
      let subledgerBase = new Decimal(0);
      for (const inv of invoices) {
        const owed = outstanding(inv); // in the invoice currency; the ageing shows base value so mixed currencies add up
        const base = owed
          .times(inv.exchangeRate.toString())
          .toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
        subledgerBase = subledgerBase.plus(base);
        const row = byParty.get(inv.partyId) ?? {
          ...empty(),
          partyName: names.get(inv.partyId) ?? '',
          currencies: new Set<string>(),
        };
        const bucket: AgeBucket = ageBucket(inv.dueDate, asOf);
        row[bucket] = row[bucket].plus(base);
        row.total = row.total.plus(base);
        row.currencies.add(inv.currency);
        byParty.set(inv.partyId, row);
      }
      const rows = [...byParty.entries()]
        .map(([partyId, r]) => ({
          partyId,
          partyName: r.partyName,
          current: r.current.toFixed(0),
          d1_30: r.d1_30.toFixed(0),
          d31_60: r.d31_60.toFixed(0),
          d61_90: r.d61_90.toFixed(0),
          d90_plus: r.d90_plus.toFixed(0),
          total: r.total.toFixed(0),
        }))
        .sort((a, b) => Number(b.total) - Number(a.total));
      const totals = rows.reduce(
        (t, r) => ({
          current: t.current + Number(r.current),
          d1_30: t.d1_30 + Number(r.d1_30),
          d31_60: t.d31_60 + Number(r.d31_60),
          d61_90: t.d61_90 + Number(r.d61_90),
          d90_plus: t.d90_plus + Number(r.d90_plus),
          total: t.total + Number(r.total),
        }),
        { current: 0, d1_30: 0, d31_60: 0, d61_90: 0, d90_plus: 0, total: 0 },
      );
      // Control check: the receivables ledger must equal the open invoices (only meaningful for the whole company).
      let arLedger: string | null = null;
      if (req.auth!.scope === 'all' && can(req, 'accounting.report.view')) {
        const ar = await db.account.findFirst({
          where: { companyId: req.auth!.companyId, systemKey: 'AR' },
        });
        if (ar) {
          const [b] = await accountBalances(db, req.auth!.companyId, { accountIds: [ar.id] });
          arLedger = (b ? b.debit.minus(b.credit) : new Decimal(0)).toFixed(0);
        }
      }
      res.json({
        asOf: asOfStr,
        rows,
        totals,
        subledger: subledgerBase.toFixed(0),
        arLedger,
        reconciled: arLedger === null ? null : arLedger === subledgerBase.toFixed(0),
      });
    }),
  );

  void addDays;
  return router;
}
